/** @jsx jsx */
import React, { useEffect, useMemo, useCallback, type PropsWithChildren } from 'react';
import { jsx } from '@compiled/react';
import { graphql, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import type { OperationType } from 'relay-runtime';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { AsyncGPAwarenessPill } from '@jira/servicedesk__jsm-grace-period-banner/src/AsyncGPAwarenessPill.tsx';
import { useTheme } from '@jira/navigation-apps__atlassian-navigation-theme/src/main.tsx';
import { TopNavStart, TopNavMiddle, TopNavEnd } from '@atlaskit/navigation-system/layout/top-nav';
import { SideNavToggleButton } from '@atlaskit/navigation-system/layout/side-nav';
import type { SideNavVisibilityChangeAnalyticsAttributes } from '@atlaskit/navigation-system';
import { useIsFhsEnabled } from '@atlaskit/navigation-system/fhs-rollout';
import { CustomTitle } from '@atlaskit/navigation-system/top-nav-items/custom-title';
import { getTokenValue } from '@atlaskit/tokens';
import { isSupportedUserAgent as isMobileUserAgent } from '@jira/platform__is-mobile-user-agent/src/index.tsx';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useSetCustomThemeData } from '@jira/navigation-apps__common/src/controllers/custom-theme-context/index.tsx';
import { CustomThemeAnalytics } from '@jira/navigation-apps__common/src/controllers/custom-theme-analytics/index.tsx';
import { EditionAwareness } from '@jira/navigation-apps__sidebar-edition-awareness/src/ui/EditionAwareness.tsx';
import Nav4AppSwitcher from '@jira/navigation-apps__topnav-app-switcher/src/ui/index.tsx';
import AsyncCustomThemeAdminFlag from '@jira/theme__custom-theme-admin-flag/src/AsyncCustomThemeAdminFlag.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { JiraHoldoutCustomData } from '@atlassian/holdout-utils/jira-holdout';
import {
	ContextualAnalyticsData,
	fireUIAnalytics,
	type UIAnalyticsEvent,
} from '@atlassian/jira-product-analytics-bridge';
import {
	PACKAGE_NAME,
	TEAM_NAME,
} from '@jira/navigation-apps__atlassian-navigation-nav4-common/src/common/constants.tsx';
import { useRelayResource } from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { V4 } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/nav-state.tsx';
import { TOP } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/navigation-container.tsx';
import { GLOBAL } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/navigation-layer.tsx';
import { getSideNavToggleButtonAnalyticEventPayload } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/side-nav.tsx';
import { IS_LEFT_SIDEBAR_COLLAPSED_DEFAULT } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/constants/index.tsx';
import { useLeftSidebarState } from '@jira/platform__controllers-layout/src/controllers/layout-controller/consumers/left-sidebar-controller/index.tsx';
import { topNav4UIStateResource } from '@jira/navigation-apps__resources/src/services/top-nav/index.tsx';
import { useSiteTitle } from './site-title/useSiteTitle.tsx';
import type { TopNavigationQuery } from './__generated__/TopNavigationQuery.graphql';
import { Nav4NotificationsData } from './notifications/Nav4NotificationsData.tsx';
import { Settings } from './settings/SettingsAction.tsx';
import { Search } from './search/index.tsx';
import { NavigationRouterListener } from './router-listener/index.tsx';
import { HelpButton } from './help/HelpButton.tsx';
import type { HelpButton_query$key } from './help/__generated__/HelpButton_query.graphql.ts';
import { ConversationAssistant } from './conversation-assistant/index.tsx';
import { ProfileNew } from './profile-new/ProfileNew.tsx';
import { ProductHomeWithQuery } from './product-home/ProductHomeWithQuery.tsx';
import { useReadyQueryReference } from './utils/useReadyQueryReference.tsx';

const sideNavToggleButtonCollapseLabel = defineMessage({
	id: 'atlassian-navigation.side-nav-toggle-button-collapse-label',

	defaultMessage: 'Collapse sidebar',
	description:
		'Label to use for side nav toggle button when it will collapse the side bar when clicked.',
});

const sideNavToggleButtonExpandLabel = defineMessage({
	id: 'atlassian-navigation.side-nav-toggle-button-expand-label',

	defaultMessage: 'Expand sidebar',
	description:
		'Label to use for side nav toggle button when it will expand the side bar when clicked.',
});

const sideNavToggleButtonCollapseLabelWithShortcut = defineMessage({
	id: 'atlassian-navigation.side-nav-toggle-button-collapse-label-with-shortcut',

	defaultMessage: 'Collapse sidebar [',
	description:
		'Label to use for side nav toggle button when it will collapse the side bar when clicked. It ends with a bracket character that reflects the keyboard shortcut that will toggle the side bar.',
});

const sideNavToggleButtonExpandLabelWithShortcut = defineMessage({
	id: 'atlassian-navigation.side-nav-toggle-button-expand-label-with-shortcut',

	defaultMessage: 'Expand sidebar [',
	description:
		'Label to use for side nav toggle button when it will expand the side bar when clicked. It ends with a bracket character that reflects the keyboard shortcut that will toggle the side bar.',
});

type HelpComponentProps = {
	QueryFragment: HelpButton_query$key | null;
};
function HelpComponent({ QueryFragment }: HelpComponentProps) {
	return <HelpButton QueryFragment={QueryFragment} />;
}

const Create = importCond<
	typeof import('@jira/navigation-apps__topnav-universal-create/src/UniversalCreate.tsx'),
	typeof import('./create/main.tsx')
>(
	'navx_onboarding_universal_create_in_jira',
	'@jira/navigation-apps__topnav-universal-create/src/UniversalCreate.tsx',
	'./create/main.tsx',
);

// Feature gate off no-op: return the raw ref, preserving original behaviour
function useQueryReferencePassthrough<TQuery extends OperationType>(
	current: PreloadedQuery<TQuery> | null,
): PreloadedQuery<TQuery> | null {
	return current;
}

const useStableQueryReference = functionWithCondition(
	() => fg('tako-5534_topnav_project_suspense_fix'),
	useReadyQueryReference,
	useQueryReferencePassthrough,
);

// ============================================================================
// EXPORTED COMPONENTS
// ============================================================================
export function TopNavigation(): JSX.Element | null {
	const { queryReference } = useRelayResource(topNav4UIStateResource);

	// Keep rendering the previous ref while a new one is in flight so route
	// changes (e.g. project navigation) don't flash the parent skeleton.
	const stableQueryReference = useStableQueryReference(queryReference);

	if (!stableQueryReference) {
		return null;
	}

	return (
		<TopNavBoundary>
			<TopNavContent queryReference={stableQueryReference} />
		</TopNavBoundary>
	);
}

// ============================================================================
// INTERNAL COMPONENTS
// ============================================================================
export function TopNavContent({
	queryReference,
}: {
	queryReference: PreloadedQuery<TopNavigationQuery>;
}): JSX.Element {
	const data = usePreloadedQuery<TopNavigationQuery>(
		graphql`
			query TopNavigationQuery(
				$atlassianAccountId: ID!
				$cloudId: ID!
				$isAnonymous: Boolean!
				$isNavStateQuerySkipped: Boolean!
				$issueKey: String!
				$shouldCheckProjectTypeForIssue: Boolean!
				$projectKey: String!
				$shouldCheckProjectType: Boolean!
				$projectIdOrKey: String!
				$shouldFetchProjectTypeForSettingsAndEA: Boolean!
				$shouldFetchProjectType: Boolean!
				$shouldFetchCSMLiveChatAgent: Boolean!
				$skipHelpButtonRelayRead: Boolean!
				$collabContextRoutingAri: String
			) {
				jira @skip(if: $isNavStateQuerySkipped) {
					navigationUIState(cloudId: $cloudId) {
						isLeftSidebarCollapsed
					}
				}
				...ProfileNew @dangerously_unaliased_fixme @skip(if: $isAnonymous)
				...Nav4NotificationsData
					@dangerously_unaliased_fixme
					@arguments(collabContextRoutingAri: $collabContextRoutingAri)
					@skip(if: $isAnonymous)
				...ProductHomeWithQuery
				...EditionAwareness_query
				...SettingsAction_query
				...HelpButton_query @dangerously_unaliased_fixme @skip(if: $skipHelpButtonRelayRead)
			}
		`,
		queryReference,
	);

	const theme = useTheme();
	const cloudId = useCloudId();
	const isSiteAdmin = useIsSiteAdmin();
	const isMobileExperience = isMobileUserAgent();
	const setCustomThemeData = useSetCustomThemeData();

	// Set custom theme data for background and highlight colors
	const customThemeData = useMemo(
		() => ({
			hasCustomBGColor: !!theme?.mode.navigation.backgroundColor,
			hasCustomHighlightColor: getHasCustomHighlightColor(theme),
			cloudId,
		}),
		[cloudId, theme],
	);

	useEffect(() => {
		setCustomThemeData(customThemeData);
	}, [setCustomThemeData, customThemeData]);

	return (
		<>
			<CustomThemeAnalytics>
				{isMobileExperience && !fg('jira_use_full_navigation_on_mobile') ? (
					<TopNavMobile data={data} />
				) : (
					<TopNavDesktop data={data} />
				)}
				<NavigationRouterListener />
				{isSiteAdmin && <AsyncCustomThemeAdminFlag />}
			</CustomThemeAnalytics>
		</>
	);
}

function TopNavDesktop({ data }: { data: TopNavigationQuery['response'] }) {
	const isFhsEnabled = useIsFhsEnabled();

	const isAnonymous = useIsAnonymous();
	const { formatMessage } = useIntl();

	const siteTitleData = useSiteTitle();
	const siteTitle = (siteTitleData.shouldShow && siteTitleData.value) || undefined;

	const setCustomThemeData = useSetCustomThemeData();

	// Set custom theme data for site title
	const desktopSiteTitleData = useMemo(
		() => ({
			hasCustomSiteTitle: !!siteTitle,
			customSiteTitleLength: siteTitle?.length || undefined,
		}),
		[siteTitle],
	);

	useEffect(() => {
		setCustomThemeData(desktopSiteTitleData);
	}, [setCustomThemeData, desktopSiteTitleData]);

	const attributes = useMemo(
		() => ({
			navigationLayer: GLOBAL,
			navigationContainer: TOP,
			navState: V4,
		}),
		[],
	);

	const onClickSideNavToggleButton = useCallback(
		(
			_: React.MouseEvent<HTMLElement>,
			analyticsEvent: UIAnalyticsEvent,
			_attributes: SideNavVisibilityChangeAnalyticsAttributes | undefined,
		) => {
			const payload = getSideNavToggleButtonAnalyticEventPayload(_attributes?.isSideNavVisible);
			fireUIAnalytics(analyticsEvent, payload);
		},
		[],
	);

	const { isCollapsed: isLeftSidebarCollapsed } = useLeftSidebarState();
	const defaultCollapsed = useMemo(() => {
		if (fg('navx-global-side-nav')) {
			return isLeftSidebarCollapsed ?? IS_LEFT_SIDEBAR_COLLAPSED_DEFAULT;
		}
		return (
			data?.jira?.navigationUIState?.isLeftSidebarCollapsed ?? IS_LEFT_SIDEBAR_COLLAPSED_DEFAULT
		);
	}, [data?.jira?.navigationUIState?.isLeftSidebarCollapsed, isLeftSidebarCollapsed]);

	const sideNavToggleButton = (
		<SideNavToggleButton
			defaultCollapsed={isFhsEnabled ? undefined : defaultCollapsed}
			onClick={onClickSideNavToggleButton}
			collapseLabel={
				isFhsEnabled
					? formatMessage(sideNavToggleButtonCollapseLabel)
					: formatMessage(sideNavToggleButtonCollapseLabelWithShortcut)
			}
			expandLabel={
				isFhsEnabled
					? formatMessage(sideNavToggleButtonExpandLabel)
					: formatMessage(sideNavToggleButtonExpandLabelWithShortcut)
			}
		/>
	);

	return (
		<ContextualAnalyticsData attributes={attributes}>
			<TopNavStart sideNavToggleButton={sideNavToggleButton}>
				<Nav4AppSwitcher />
				<ProductHomeWithQuery queryReference={data} />
				{siteTitle && <CustomTitle>{siteTitle}</CustomTitle>}
			</TopNavStart>
			<TopNavMiddle>
				<Search />
				<Create />
			</TopNavMiddle>
			<TopNavEnd>
				{fg('jsm_gp_awareness_pill') && <AsyncGPAwarenessPill analyticsOrigin="navBar" />}
				<EditionAwareness editionAwarenessQueryFragment={data} />
				{!isAnonymous && <ConversationAssistant />}
				{!isAnonymous && <Nav4NotificationsData queryReference={data} />}
				<HelpComponent QueryFragment={isAnonymous ? null : data} />
				<Settings settingsActionQueryFragment={data} />
				<ProfileNew queryReference={data} />
			</TopNavEnd>
		</ContextualAnalyticsData>
	);
}

function TopNavMobile({ data }: { data: TopNavigationQuery['response'] }) {
	const isAnonymous = useIsAnonymous();
	const siteTitleDataHook = useSiteTitle();
	const siteTitle = (siteTitleDataHook.shouldShow && siteTitleDataHook.value) || undefined;
	const setCustomThemeData = useSetCustomThemeData();

	// Set custom theme data for site title
	const mobileSiteTitleData = useMemo(
		() => ({
			hasCustomSiteTitle: !!siteTitle,
			customSiteTitleLength: siteTitle?.length || undefined,
		}),
		[siteTitle],
	);

	useEffect(() => {
		setCustomThemeData(mobileSiteTitleData);
	}, [setCustomThemeData, mobileSiteTitleData]);

	const attributes = useMemo(
		() => ({
			navigationLayer: GLOBAL,
			navigationContainer: TOP,
			navState: V4,
		}),
		[],
	);

	return (
		<ContextualAnalyticsData attributes={attributes}>
			<TopNavStart sideNavToggleButton={null}>
				<ProductHomeWithQuery queryReference={data} />

				{siteTitle && <CustomTitle>{siteTitle}</CustomTitle>}
			</TopNavStart>
			<TopNavMiddle>
				<Search />
				<Create />
			</TopNavMiddle>
			<TopNavEnd>
				{fg('jsm_gp_awareness_pill') && <AsyncGPAwarenessPill analyticsOrigin="navBar" />}
				{!isAnonymous && <Nav4NotificationsData queryReference={data} />}
				<ProfileNew queryReference={data} />
			</TopNavEnd>
		</ContextualAnalyticsData>
	);
}

function TopNavBoundary({ children }: PropsWithChildren) {
	return (
		<UFOSegment name="atlaskit-atlassian-navigation-nav4">
			<JiraHoldoutCustomData />
			<JSErrorBoundary
				id="AtlaskitAtlassianNavigationNav4"
				packageName={PACKAGE_NAME}
				teamName={TEAM_NAME}
				fallback="flag"
			>
				{children}
			</JSErrorBoundary>
		</UFOSegment>
	);
}

// ============================================================================
// UTILITIES
// ============================================================================

/**
 * Check if the theme has a custom highlight color
 */
function getHasCustomHighlightColor(theme: ReturnType<typeof useTheme>) {
	if (!theme) return false;

	// Default create button background color in Nav4 is #0052CC
	// #0052CC is a legacy brand blue. In the non-legacy tokens, the general replacement is:
	//   • Token name: color.link
	//   • CSS variable: var(--ds-link)
	//   • Current hex in atlassian-light: #0C66E4
	// If the create button background color is different from this value, we consider it as a custom highlight color
	const tokenHighlightColor = getTokenValue('color.link');

	return (
		theme.mode.create.default.backgroundColor !== tokenHighlightColor &&
		// Failsafe check in case the token value is not what we expect
		theme.mode.create.default.backgroundColor !== '#0052CC'
	);
}

export default TopNavigation;
